import React from 'react';
import { Box, Grid, WithStyles } from '@material-ui/core';
import RefreshRounded from '@material-ui/icons/RefreshRounded';
import styles from './styles';
import Switch from '../Switch';
import HubForm from '../HubForm';
import {
  HubLock,
  HubLockUserCodeUpdate,
  ScheduledCode,
} from '../../redux/types/hubs';
import {
  createHubLockMasterCodeRequest,
  deleteHubLockCodeRequest,
  updateHubLockAutoLockRequest,
  updateHubLockCodesRequest,
  updateHubLockMasterCodeRequest,
  updateHubLockStatusRequest,
  updateHubLockScheduledCodesRequest,
  deleteHubLockScheduledCodeRequest,
} from '../../redux/actions/hubs/hubLocks';
import { ActionPayload } from '../../types/main';
import LockIcon from '../Icons/LockIcon';
import HubLockUserCodesForm from '../HubLockUserCodesForm';
import HubScheduledCodesForm from '../HubScheduledCodesForm';
import { PropertyDetailsInfo } from '../../redux/types/properties';
import CustomButton from '../CustomButton';
import { refreshHubDevicesRequest } from '../../redux/actions/hubs/hubs';
import { usePermissions } from '../../hooks/usePermissions';

type Props = {
  hubId: string;
  record: HubLock;
  property: PropertyDetailsInfo,
  updating: boolean;
  refreshLoading: boolean;
  updateHubLockStatus: (
    payload: ActionPayload<typeof updateHubLockStatusRequest>,
  ) => void;
  updateHubLockAutoLock: (
    payload: ActionPayload<typeof updateHubLockAutoLockRequest>,
  ) => void;
  createHubLockMasterCode: (
    payload: ActionPayload<typeof createHubLockMasterCodeRequest>,
  ) => void;
  updateHubLockMasterCode: (
    payload: ActionPayload<typeof updateHubLockMasterCodeRequest>,
  ) => void;
  updateHubLockCodes: (
    payload: ActionPayload<typeof updateHubLockCodesRequest>,
  ) => void;
  deleteHubLockCode: (
    payload: ActionPayload<typeof deleteHubLockCodeRequest>,
  ) => void;
  updateHubLockScheduledCodes: (
    payload: ActionPayload<typeof updateHubLockScheduledCodesRequest>,
  ) => void;
  deleteHubLockScheduledCode: (
    payload: ActionPayload<typeof deleteHubLockScheduledCodeRequest>,
  ) => void;
  refreshHubDevices: (hubId: ActionPayload<typeof refreshHubDevicesRequest>) => void;
} & WithStyles<typeof styles>;

const HubLockForm: React.FC<Props> = (props: Props) => {
  const {
    hubId,
    record,
    property,
    updating,
    refreshLoading,
    updateHubLockStatus,
    deleteHubLockCode,
    updateHubLockCodes,
    updateHubLockScheduledCodes,
    deleteHubLockScheduledCode,
    refreshHubDevices,
  } = props;

  const { canEdit } = usePermissions();

  const {
    id: deviceId,
    properties: {
      userCodes,
      scheduledCodes,
      status: lockStatus,
    },
  } = record;

  // const autoLock = lockTimeoutEnabled === undefined ? true : lockTimeoutEnabled;

  const handleLockStatus = () => {
    const status = lockStatus === 'locked' ? 'unlocked' : 'locked';
    updateHubLockStatus({ hubId, status, deviceId });
  };

  // const handleAutoLock = () => {
  //   updateHubLockAutoLock({ hubId, autoLock: !autoLock });
  // };

  const updateHubLockCodesHandler = (codes: HubLockUserCodeUpdate[]) => {
    updateHubLockCodes({ hubId, codes });
  };

  const deleteHubLockCodeHandler = (codeKey: string) => {
    deleteHubLockCode({ hubId, codeKey });
  };

  const updateHubScheduledCodesHandler = (scheduledCodes: ScheduledCode[]) => {
    updateHubLockScheduledCodes({ hubId, scheduledCodes });
  };

  const deleteHubScheduledCodeHandler = (slot: number) => {
    deleteHubLockScheduledCode({ hubId, slot });
  };

  const refreshDevice = () => {
    if (hubId) {
      refreshHubDevices(hubId);
    }
  };

  return (
    <HubForm
      title={{
        title: 'Lock',
        recordName: record.name,
        icon: <LockIcon />,
      }}
    >
      <Grid item xs={12}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Switch
            title="Status"
            valueName="Unlock"
            secondValueName="Lock"
            value={lockStatus === 'locked'}
            setValue={handleLockStatus}
            disabled={updating || refreshLoading || !canEdit}
          />
          <CustomButton
            variant="orange"
            onClick={refreshDevice}
            endIcon={<RefreshRounded />}
            disabled={updating || refreshLoading || !canEdit}
          >
            Refresh Device
          </CustomButton>
        </Box>
      </Grid>
      {/* <Grid item xs={6}>
        <Switch
          title="Auto-Lock"
          valueName="Off"
          secondValueName="On"
          value={Boolean(autoLock)}
          setValue={handleAutoLock}
          disabled={updating || refreshLoading}
        />
      </Grid> */}
      <Grid item xs={12}>
        <HubLockUserCodesForm
          userCodes={userCodes}
          deleteHubLockCode={deleteHubLockCodeHandler}
          updateHubLockCodes={updateHubLockCodesHandler}
          updating={updating}
        />
      </Grid>

      <Grid item xs={12}>
        <HubScheduledCodesForm
          scheduledCodes={scheduledCodes}
          deleteHubScheduledCode={deleteHubScheduledCodeHandler}
          updateHubScheduledCodes={updateHubScheduledCodesHandler}
          updating={updating}
          property={property}
        />
      </Grid>
    </HubForm>
  );
};

export default HubLockForm;
